/**
 * Composable for KanbanCard logic
 * Extracts all computed properties and helper functions from KanbanCard.vue
 */
import { computed } from 'vue';
import { useStore } from 'vuex';

// Channel type to icon mapping
const CHANNEL_ICON_MAP = {
  'Channel::Api': 'ri-cloudy-fill',
  'Channel::Email': 'ri-mail-fill',
  'Channel::FacebookPage': 'ri-messenger-fill',
  'Channel::Telegram': 'ri-telegram-fill',
  'Channel::TwitterProfile': 'ri-twitter-x-fill',
  'Channel::WebWidget': 'ri-global-fill',
  'Channel::Whatsapp': 'ri-whatsapp-fill',
  'Channel::Instagram': 'ri-instagram-fill',
  'Channel::Voice': 'ri-phone-fill',
  api: 'ri-cloudy-fill',
  email: 'ri-mail-fill',
  facebook: 'ri-messenger-fill',
  telegram: 'ri-telegram-fill',
  twitter: 'ri-twitter-x-fill',
  web_widget: 'ri-global-fill',
  whatsapp: 'ri-whatsapp-fill',
  instagram: 'ri-instagram-fill',
  voice: 'ri-phone-fill',
};

// Priority mapping
const PRIORITY_MAP = {
  urgent: 'Urgente',
  high: 'Alta',
  medium: 'Média',
  low: 'Baixa',
  none: 'Nenhuma',
};

// Status mapping
const STATUS_MAP = {
  open: 'Aberto',
  resolved: 'Resolvido',
  pending: 'Pendente',
  snoozed: 'Adiado',
  all: 'Todos',
};

/**
 * Get channel icon based on channel type
 */
export function getChannelIcon(channelType, provider) {
  if (channelType === 'Channel::Email' || channelType === 'email') {
    if (provider === 'microsoft') return 'ri-microsoft-fill';
    if (provider === 'google') return 'ri-google-fill';
  }
  return CHANNEL_ICON_MAP[channelType] || 'ri-global-fill';
}

/**
 * Get channel color class based on icon
 */
export function getChannelColorClass(icon) {
  if (icon.includes('whatsapp')) return 'text-green-500';
  if (icon.includes('messenger') || icon.includes('facebook'))
    return 'text-blue-500';
  if (icon.includes('instagram')) return 'text-pink-500';
  if (icon.includes('telegram')) return 'text-cyan-500';
  if (icon.includes('twitter')) return 'text-sky-500';
  if (icon.includes('mail')) return 'text-amber-500';
  if (icon.includes('microsoft')) return 'text-blue-600';
  if (icon.includes('google')) return 'text-red-500';
  if (icon.includes('phone')) return 'text-purple-500';
  return 'text-slate-500 dark:text-slate-400';
}

/**
 * Format attribute value based on type
 */
export function formatAttributeValue(value, format, prefix = '', suffix = '') {
  if (!value) return '--';

  let formatted = value;

  switch (format) {
    case 'number':
      formatted = parseFloat(value).toLocaleString('pt-BR');
      break;
    case 'currency':
      formatted = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(parseFloat(value));
      break;
    case 'date': {
      const date = new Date(value);
      formatted = date.toLocaleDateString('pt-BR');
      break;
    }
    case 'datetime': {
      const datetime = new Date(value);
      formatted = datetime.toLocaleString('pt-BR');
      break;
    }
    case 'boolean':
      formatted = value === true || value === 'true' ? 'Sim' : 'Não';
      break;
    case 'percentage':
      formatted = `${parseFloat(value).toFixed(1)}%`;
      break;
    default:
      formatted = String(value);
  }

  return `${prefix}${formatted}${suffix}`;
}

/**
 * Main composable for KanbanCard
 */
export function useKanbanCard(conversation, mapping, funnelId) {
  const store = useStore();

  // Contact computed
  const currentContact = computed(() => {
    if (conversation.value?.contact) {
      return conversation.value.contact;
    }

    if (conversation.value?.meta?.sender?.id) {
      const contact = store.getters['contacts/getContact'](
        conversation.value.meta.sender.id
      );
      if (contact) return contact;
    }

    if (conversation.value?.meta?.sender) {
      return conversation.value.meta.sender;
    }

    return null;
  });

  // Inbox computed
  const inbox = computed(() => {
    if (!conversation.value?.inbox_id) return null;
    return (
      store.getters['inboxes/getInbox'](conversation.value.inbox_id) || null
    );
  });

  // Channel icon computed
  const inboxChannelIcon = computed(() => {
    let channelType = inbox.value?.channel_type;
    if (!channelType && conversation.value?.channel) {
      channelType = conversation.value.channel;
    }
    return getChannelIcon(channelType, inbox.value?.provider);
  });

  // Channel color class computed
  const inboxChannelColorClass = computed(() => {
    return getChannelColorClass(inboxChannelIcon.value);
  });

  // Assignee computed
  const assignee = computed(() => {
    if (conversation.value?.assignee) {
      return conversation.value.assignee;
    }

    if (conversation.value?.meta?.assignee) {
      return conversation.value.meta.assignee;
    }

    if (conversation.value?.assignee_id) {
      const agent = store.getters['agents/getAgentById'](
        conversation.value.assignee_id
      );
      if (agent) return agent;
    }

    return null;
  });

  // Labels computed
  const activeLabels = computed(() => {
    const labels = conversation.value?.labels;

    // Sem labels
    if (!labels || labels.length === 0) {
      return [];
    }

    // Labels já são objetos completos com color
    if (
      typeof labels[0] === 'object' &&
      labels[0] !== null &&
      (labels[0].color || labels[0].title)
    ) {
      return labels.map(label => ({
        id: label.id || label.title,
        title: label.title || label.name,
        color: label.color || '#6366f1',
      }));
    }

    // Labels são strings (títulos) - buscar dados completos do store
    const accountLabels = store.getters['labels/getLabels'] || [];

    return labels
      .map(labelTitle => {
        // Se for string, buscar no store
        if (typeof labelTitle === 'string') {
          const found = accountLabels.find(
            l => l.title === labelTitle || l.name === labelTitle
          );
          if (found) {
            return {
              id: found.id || labelTitle,
              title: found.title || found.name || labelTitle,
              color: found.color || '#6366f1',
            };
          }
          // Label não encontrada no store, criar placeholder
          return {
            id: labelTitle,
            title: labelTitle,
            color: '#6366f1',
          };
        }
        return null;
      })
      .filter(Boolean);
  });

  // Priority computed
  const priorityColor = computed(() => {
    if (conversation.value?.priority === 'urgent') {
      return 'bg-n-ruby-4 text-n-ruby-10';
    }
    return 'bg-n-slate-4 text-n-slate-11';
  });

  const priorityLabel = computed(() => {
    return (
      PRIORITY_MAP[conversation.value?.priority] ||
      conversation.value?.priority ||
      ''
    );
  });

  // Status computed - using Chatwoot design system colors
  const statusColor = computed(() => {
    switch (conversation.value?.status) {
      case 'open':
        return 'text-n-blue-11 bg-n-blue-3';
      case 'resolved':
        return 'text-n-teal-11 bg-n-teal-3';
      case 'pending':
        return 'text-n-amber-11 bg-n-amber-3';
      case 'snoozed':
        return 'text-n-violet-11 bg-n-violet-3';
      default:
        return 'text-n-slate-11 bg-n-alpha-2';
    }
  });

  const statusLabel = computed(() => {
    return (
      STATUS_MAP[conversation.value?.status] || conversation.value?.status || ''
    );
  });

  // Unread count
  const unreadCount = computed(() => {
    return conversation.value?.unread_count || 0;
  });

  // Total value computed
  const totalValue = computed(() => {
    const products = conversation.value?.conversation_products || [];
    if (products.length === 0) return 0;

    const filteredProducts = funnelId?.value
      ? products.filter(
          product => !product.funnel_id || product.funnel_id === funnelId.value
        )
      : products;

    return filteredProducts.reduce((sum, product) => {
      return sum + (parseFloat(product.amount) || 0);
    }, 0);
  });

  // Checklist progress
  const checklistProgress = computed(() => {
    const checklist = mapping?.value?.checklist || [];
    const done = checklist.filter(item => item.done).length;
    return {
      done,
      total: checklist.length,
      percentage: checklist.length
        ? Math.round((done / checklist.length) * 100)
        : 0,
    };
  });

  // Assigned agents
  const assignedAgents = computed(() => {
    const agents = [];

    if (assignee.value) {
      agents.push({ ...assignee.value, role: 'primary' });
    }

    if (mapping?.value?.assigned_agents?.length) {
      mapping.value.assigned_agents.forEach(agent => {
        if (!agents.find(a => a.id === agent.id)) {
          agents.push(agent);
        }
      });
    }

    return agents;
  });

  const visibleAgents = computed(() => {
    return assignedAgents.value.slice(0, 3);
  });

  const additionalAgentsCount = computed(() => {
    return Math.max(0, assignedAgents.value.length - 3);
  });

  // Attachment count
  const attachmentCount = computed(() => {
    return (
      conversation.value?.messages?.reduce((count, msg) => {
        return count + (msg.attachments?.length || 0);
      }, 0) || 0
    );
  });

  // Appointment datetime
  const appointmentDateTime = computed(() => {
    const appointments = conversation.value?.conversation_appointments || [];
    if (appointments.length === 0) return null;

    // Filter appointments that have a scheduled_at date
    const validAppointments = appointments.filter(apt => apt.scheduled_at);
    if (validAppointments.length === 0) return null;

    const now = new Date();

    // First, try to find future appointments (scheduled or confirmed)
    const futureAppointments = validAppointments
      .filter(apt => {
        const aptDate = new Date(apt.scheduled_at);
        const isFuture =
          aptDate >= now || aptDate.toDateString() === now.toDateString();
        const isActiveStatus =
          !apt.status ||
          apt.status === 'scheduled' ||
          apt.status === 'confirmed';
        return isFuture && isActiveStatus;
      })
      .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));

    // If we have future appointments, show the next one
    if (futureAppointments.length > 0) {
      const nextAppointment = futureAppointments[0];
      const date = new Date(nextAppointment.scheduled_at);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${day}/${month} ${hours}:${minutes}`;
    }

    // Otherwise, show the most recent appointment (regardless of status)
    const sortedByDate = [...validAppointments].sort(
      (a, b) => new Date(b.scheduled_at) - new Date(a.scheduled_at)
    );

    const mostRecent = sortedByDate[0];
    const date = new Date(mostRecent.scheduled_at);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}/${month} ${hours}:${minutes}`;
  });

  // Custom attributes
  const visibleCustomAttributes = computed(() => {
    const funnel = conversation.value?.funnel || {};
    const visibleAttrs = funnel.settings?.visible_attributes || [];

    if (!visibleAttrs.length) return [];

    const customAttributes = conversation.value?.custom_attributes || {};
    const displayConfig = funnel.settings?.attributes_display || {};
    const formatConfigs = funnel.settings?.attribute_formats || {};

    return visibleAttrs
      .map(attrKey => {
        const value = customAttributes[attrKey];
        const formatConfig = formatConfigs[attrKey] || {};

        return {
          key: attrKey,
          value,
          label:
            formatConfig.label ||
            attrKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          icon: formatConfig.icon,
          color: formatConfig.color,
          prefix: formatConfig.prefix,
          suffix: formatConfig.suffix,
          format: formatConfig.format || 'text',
        };
      })
      .filter(attr => {
        return displayConfig.show_empty !== false || attr.value;
      });
  });

  const attributeDisplayStyle = computed(() => {
    const funnel = conversation.value?.funnel || {};
    const displayConfig = funnel.settings?.attributes_display || {};
    return displayConfig.style || 'badge';
  });

  const attributeDisplayPosition = computed(() => {
    const funnel = conversation.value?.funnel || {};
    const displayConfig = funnel.settings?.attributes_display || {};
    return displayConfig.position || 'bottom';
  });

  // Message preview
  const messagePreview = computed(() => {
    const lastMessage = conversation.value?.last_non_activity_message;
    if (!lastMessage) return '';

    const content = lastMessage.content || '';
    return content.length > 100 ? content.substring(0, 100) + '...' : content;
  });

  return {
    // Contact & Inbox
    currentContact,
    inbox,
    inboxChannelIcon,
    inboxChannelColorClass,

    // Assignee
    assignee,
    assignedAgents,
    visibleAgents,
    additionalAgentsCount,

    // Labels
    activeLabels,

    // Priority
    priorityColor,
    priorityLabel,

    // Status
    statusColor,
    statusLabel,

    // Counts
    unreadCount,
    attachmentCount,

    // Value & Appointments
    totalValue,
    appointmentDateTime,

    // Checklist
    checklistProgress,

    // Custom attributes
    visibleCustomAttributes,
    attributeDisplayStyle,
    attributeDisplayPosition,

    // Message
    messagePreview,

    // Helpers
    formatAttributeValue,
  };
}
